import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Tag, Search, X } from "lucide-react";
import { fetchPosts, fetchCategories } from "@/lib/api";
import NewsletterForm from "@/components/NewsletterForm";
import useSEO from "@/lib/useSEO";

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; }
};

export default function Blog() {
  useSEO({
    title: "Creator Legal Blog — YouTube, TikTok, Brand Deals & Trademarks | Upload Counsel",
    description: "Plain-English legal playbooks for YouTubers, TikTokers, streamers, podcasters, and online creators. Platform disputes, brand deals, IP, business setup, reputation, monetization.",
    canonical: "https://uploadlawyer.com/blog",
  });
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [pData, cData] = await Promise.all([
          fetchPosts({
            category: active === "All" ? undefined : active,
            q: debouncedQuery || undefined,
          }),
          fetchCategories(),
        ]);
        setPosts(pData.posts || []);
        setCats(cData.categories || []);
      } catch (e) {
        console.error("Failed to load blog data:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [active, debouncedQuery]);

  const featured = posts.find((p) => p.featured);
  const rest = posts.filter((p) => !featured || p.slug !== featured.slug);

  return (
    <div data-testid="page-blog">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 glow-tl" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-12 md:pt-32">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">The Brief</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl font-extrabold text-white tracking-tight max-w-3xl">
            Creator-economy legal intelligence.
          </h1>
          <p className="mt-6 text-slate-300 text-lg max-w-2xl leading-relaxed">
            Plain-English playbooks on platform disputes, brand deals, IP, business setup, reputation, and monetization. Built for operators.
          </p>
        </div>
      </section>

      {/* Search + Category pills */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-6 relative max-w-2xl">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search playbooks by topic, platform, or keyword…"
              data-testid="blog-search"
              className="w-full bg-[#121419] border border-[#272B36] focus:border-[#D9B36C] outline-none rounded-full pl-11 pr-11 py-3 text-white text-sm placeholder:text-slate-500"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white" data-testid="blog-search-clear">
                <X size={16} />
              </button>
            )}
          </div>

          <Link
            to="/resources/brand-deal-checklist"
            data-testid="lead-magnet-banner"
            className="group mb-10 block rounded-2xl border border-[#D9B36C]/30 bg-gradient-to-r from-[#14161D] via-[#121419] to-[#14161D] p-5 md:p-6 hover:border-[#D9B36C]/60 transition-colors"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Free resource</p>
                <p className="mt-1 font-display font-semibold text-white text-lg">The Brand Deal Redline Checklist — 10 contract redlines every creator should demand.</p>
              </div>
              <span className="inline-flex items-center gap-2 text-[#D9B36C] font-medium text-sm whitespace-nowrap group-hover:gap-3 transition-all">
                Unlock with email →
              </span>
            </div>
          </Link>
          <div className="flex flex-wrap gap-2" data-testid="blog-category-filters">
            <button
              data-testid="cat-pill-all"
              onClick={() => setActive("All")}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                active === "All"
                  ? "bg-[#D9B36C] text-black border-[#D9B36C]"
                  : "bg-[#121419] text-slate-300 border-[#272B36] hover:border-[#D9B36C]/50"
              }`}
            >
              All Posts
            </button>
            {cats.map((c) => (
              <button
                key={c.name}
                data-testid={`cat-pill-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setActive(c.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  active === c.name
                    ? "bg-[#D9B36C] text-black border-[#D9B36C]"
                    : "bg-[#121419] text-slate-300 border-[#272B36] hover:border-[#D9B36C]/50"
                }`}
              >
                {c.name} <span className="text-slate-500">{c.count}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured && active === "All" && (
        <section className="relative pt-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured-card"
              className="beam-card group block rounded-3xl border border-[#272B36] bg-[#121419] overflow-hidden hover:border-[#D9B36C]/50 transition-colors"
            >
              <div className="grid lg:grid-cols-2">
                <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                  <img
                    src={featured.cover_image}
                    alt={featured.title}
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-transparent to-transparent" />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <span className="inline-flex w-fit items-center gap-1 px-3 py-1 rounded-full bg-[#D9B36C]/15 border border-[#D9B36C]/30 text-[#D9B36C] text-xs font-semibold uppercase tracking-widest">
                    Featured · {featured.category}
                  </span>
                  <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">{featured.title}</h2>
                  <p className="mt-4 text-slate-300 leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-6 flex items-center gap-4 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Calendar size={12} /> {formatDate(featured.published_at)}</span>
                    <span>·</span>
                    <span>{featured.author}</span>
                  </div>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-[#D9B36C] group-hover:gap-2 transition-all">
                    Read playbook <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="relative py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <p className="text-slate-500" data-testid="blog-loading">Loading…</p>
          ) : posts.length === 0 ? (
            <p className="text-slate-500" data-testid="blog-empty">No posts in this category yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {(active === "All" ? rest : posts).map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="beam-card group rounded-2xl border border-[#272B36] bg-[#121419] overflow-hidden hover:border-[#D9B36C]/50 transition-colors flex flex-col"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121419]/95 via-[#121419]/30 to-transparent" />
                    <span className="absolute top-4 left-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0B0C10]/80 backdrop-blur border border-[#272B36] text-slate-200 text-[10px] font-semibold uppercase tracking-widest">
                      <Tag size={10} /> {p.category}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display text-xl font-semibold text-white leading-tight tracking-tight">{p.title}</h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed flex-1">{p.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                      <span>{formatDate(p.published_at)}</span>
                      <span className="text-[#D9B36C] group-hover:translate-x-0.5 transition-transform">Read →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="relative py-20 border-t border-[#272B36]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Newsletter</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">The Brief, in your inbox.</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            One short read per week on creator-economy legal moves. No spam. Unsubscribe in one click.
          </p>
          <div className="mt-8 max-w-md mx-auto">
            <NewsletterForm source="blog-index" />
          </div>
        </div>
      </section>
    </div>
  );
}
